import { useCallback, useEffect, useState } from "react";
import { useFilter } from "../../../context/FilterContext";
import useApiHook from "../../../utils/useApi";
import DataTable, { type TableColumn } from "react-data-table-component";
import Loading from "../loading/Loading";
import { customStyles_T } from "../../../utils/table_utils";
import type {
  ApiSettlementsResponse,
  TransactionCycle,
} from "../../../interface/interfaces";
import { formatCurrency } from "../../../utils/utils";
import { Eye, Download, CreditCard, X } from "lucide-react";
import LoadSettlementsTransactions from "./LoadSettlementsTransactions";

const LoadSettlementsTable = () => {
  const { startDate, endDate, type, searchQuery } = useFilter();

  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<TransactionCycle | null>(null);
  const [showTransactionsModal, setShowTransactionsModal] =
    useState<boolean>(false);

  const { data, isLoading, makeRequest } = useApiHook<ApiSettlementsResponse>(
    "/dashboard/settlements",
    {
      method: "get",
    }
  );

  useEffect(() => {
    fetchSettlements();
  }, [startDate, endDate, type, searchQuery]);

  const fetchSettlements = useCallback(async () => {
    setHasError(false);
    setErrorMessage("");

    const params = {
      page,
      size: perPage,
      search: searchQuery || "",
      startDate: startDate || "",
      endDate: endDate || "",
      type: type || "",
    };

    const response = await makeRequest(params);

    // Check for API errors
    if (response?.data?.statusCode !== "00") {
      setHasError(true);
      setErrorMessage(response?.data?.description || "An error occurred");
      setTotalRows(0);
    } else if (response?.data?.data) {
      setTotalRows(response.data.data.totalCount || 0);
    }
  }, [page, perPage, searchQuery, startDate, endDate, type]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (!data?.data?.data || data.data.data.length === 0) {
      alert("No data to export");
      return;
    }

    const csvHeaders = [
      "Reference",
      "Amount",
      "Transaction Count",
      "Cycle Start Date",
      "Cycle End Date",
      "Status",
      "Payment Status",
      "Payment Reference",
      "Settlement Date",
    ];

    const csvData = data.data.data.map((row: TransactionCycle) => [
      row.reference,
      row.amount,
      row.transactionCount,
      row.cycleStartDate,
      row.cycleEndDate,
      row.status,
      row.paymentStatus,
      row.paymentReference || "N/A",
      row.settlementDate,
    ]);

    const csvContent = [
      csvHeaders.join(","),
      ...csvData.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `settlements_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewMore = (row: TransactionCycle) => {
    setSelectedRow(row);
    setShowViewModal(true);
  };

  const handleSeeTransactions = (row: TransactionCycle) => {
    setSelectedRow(row);
    setShowTransactionsModal(true);
  };

  const columns: TableColumn<TransactionCycle>[] = [
    {
      name: "Reference",
      selector: (row) => row.reference,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-gray-900">{row.reference}</div>
      ),
      width: "300px",
    },

    {
      name: "Amount",
      selector: (row) => row.amount,
      sortable: true,
      cell: (row) => (
        <div className="font-medium">{formatCurrency(row.amount, "NGN")}</div>
      ),
      width:'150px'
    },

    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <div
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            row.status === "SUCCESSFUL"
              ? "bg-green-100 text-green-800"
              : row.status === "FAILED"
              ? "bg-red-100 text-red-800"
              : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {row.status}
        </div>
      ),
      width:'200px'
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleViewMore(row)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
          >
            <Eye size={14} />
            View More
          </button>
          <button
            onClick={() => handleSeeTransactions(row)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors"
          >
            <CreditCard size={14} />
            Transactions
          </button>
        </div>
      ),
      width: "300px",
      ignoreRowClick: true,
    },
    {
      name: "Settlement Date",
      selector: (row) => row.settlementDate,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-gray-900">{row.settlementDate}</div>
      ),
      width: "240px",
    },
  ];

  return (
    <section>
      {/* Export Button */}
      <div className="mb-4 flex justify-end">
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
          disabled={!data?.data?.data || data.data.data.length === 0}
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>
      {hasError ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
          <p className="font-medium">Error</p>
          <p className="text-sm">{errorMessage}</p>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data?.data?.data || []}
          progressPending={isLoading}
          progressComponent={<Loading />}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          highlightOnHover
          responsive
          persistTableHead
          customStyles={customStyles_T}
          noDataComponent={
            <div className="p-6 text-center text-gray-500">
              No Settlements found
            </div>
          }
        />
      )}
      {/* View More Modal */}
      {showViewModal && selectedRow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Settlement Details
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference
                  </label>
                  <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {selectedRow.reference}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md font-medium">
                    {formatCurrency(selectedRow.amount, "NGN")}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transaction Count
                  </label>
                  <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {selectedRow.transactionCount}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        selectedRow.status === "SUCCESSFUL"
                          ? "bg-green-100 text-green-800"
                          : selectedRow.status === "FAILED"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {selectedRow.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cycle Start Date
                  </label>
                  <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {new Date(selectedRow.cycleStartDate).toLocaleString()}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cycle End Date
                  </label>
                  <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {new Date(selectedRow.cycleEndDate).toLocaleString()}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Status
                  </label>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        selectedRow.paymentStatus === "PAID"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {selectedRow.paymentStatus}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Reference
                  </label>
                  <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {selectedRow.paymentReference || "N/A"}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Settlement Date
                  </label>
                  <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {new Date(selectedRow.settlementDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settlement Transactions Modal */}
      {showTransactionsModal && selectedRow && (
        <LoadSettlementsTransactions
          ref={selectedRow.reference}
          setShowTransactionsModal={setShowTransactionsModal}
        />
      )}
    </section>
  );
};

export default LoadSettlementsTable;
